import { useId } from 'react'

import { Icon } from '@/shared/ui/icons'
import { useUpdateAccountLanguage } from '@/shared/session/session'

import { useI18n } from './I18nProvider'
import { LOCALES, NATIVE_NAME_ID, isLocale } from './locale'

export interface LanguageSwitcherProps {
  /**
   * Authenticated shells persist to the account. The sign-in screen persists
   * only to this browser's validated login hint: before login there is no
   * current account subject for the server command.
   */
  persist?: boolean
}

/**
 * The language control: a globe, the language's own name, and a chevron.
 *
 * IT SAYS THE LANGUAGE IN THAT LANGUAGE. A flag stood here and was wrong about
 * what it claimed — a flag names a state, and neither Hebrew nor English is
 * one: the Union Jack is not the English language and Arabic has no flag at
 * all. An endonym has no such problem, and it is also the only form that works
 * for the reader this control exists for. Someone stranded in a script they
 * cannot read finds `English` by the shape of its letters, without decoding a
 * word; `אנגלית` would be the same trap in the other direction, which is why
 * the names are never translated.
 *
 * The globe carries no meaning the text does not already carry. It is here to
 * be FOUND — a glyph is what the eye lands on when it is scanning chrome rather
 * than reading it — and it is the world rather than letterforms because
 * `languages` already means turning a record from one language into another.
 *
 * The interactive element is still a native `<select>`, stretched over that
 * chrome and made transparent. That is deliberate: the platform's own option
 * list, keyboard handling, disabled semantics and screen-reader announcement
 * ship as they are. A hand-rolled listbox would have to re-implement all four,
 * and would get one of them wrong on a browser nobody here tests — in a control
 * whose failure mode is a clinician stranded in a language they cannot read.
 */
export function LanguageSwitcher({ persist = false }: LanguageSwitcherProps) {
  const messageId = useId()
  const { locale, setLoginLocale, applyPersistedLocale, t } = useI18n()
  const updateLanguage = useUpdateAccountLanguage()

  const message = updateLanguage.isPending
    ? t('language.saving')
    : updateLanguage.isError
      ? t('language.saveFailed')
      : null

  return (
    <div className="language-switcher">
      <span className="language-switcher__control">
        {/* Decorative as a whole: the select stretched over it carries the
            accessible name AND the current value, so a name announced here
            would say the same thing twice. `lang` still earns its place on an
            aria-hidden node — it is what picks the font and the digit shaping
            for a name written in a script the surrounding page is not in. */}
        <span
          className="language-switcher__face"
          // Which language the closed control STATES, in a form a test can
          // read. The alternative is asserting on the rendered name, which
          // fails on a missing font and passes on the wrong language.
          data-locale={locale}
          aria-hidden="true"
        >
          {/* `lg` like the header glyphs either side of it: the default `md` globe
              was the one smaller icon on their shared line. */}
          <Icon name="globe" size="lg" className="language-switcher__globe" />
          <span className="language-switcher__name" lang={locale}>
            {t(NATIVE_NAME_ID[locale])}
          </span>
          <Icon name="chevron-down" size="sm" className="language-switcher__caret" />
        </span>
        <select
          className="language-switcher__select"
          // The word is gone from the chrome, not from the product: this is
          // the control's name to assistive technology and its tooltip to a
          // pointer, both localized.
          aria-label={t('language.label')}
          title={t('language.label')}
          value={locale}
          // Disabled because a mutation IT started is in flight, which is a
          // wait and not a refusal: `aria-busy` says so, and `controls.css`
          // draws the progress cursor only where the two are stated together.
          disabled={updateLanguage.isPending}
          aria-busy={updateLanguage.isPending}
          aria-describedby={message ? messageId : undefined}
          onChange={(event) => {
            const next = event.target.value
            if (!isLocale(next) || next === locale) {
              return
            }
            if (!persist) {
              setLoginLocale(next)
              return
            }
            updateLanguage.mutate(next, {
              onSuccess: (context) => {
                applyPersistedLocale(context.language, context.direction)
              },
            })
          }}
        >
          {/* `lang` per option, because each one is written in its own script:
              without it a screen reader pronounces `עברית` with the voice of
              whatever language the page is in, which is the one language its
              reader has already said they cannot use. */}
          {LOCALES.map((value) => (
            <option key={value} value={value} lang={value}>
              {t(NATIVE_NAME_ID[value])}
            </option>
          ))}
        </select>
      </span>
      {message ? (
        <span
          id={messageId}
          className={
            updateLanguage.isError
              ? 'language-switcher__message language-switcher__message--error'
              : 'language-switcher__message'
          }
          role={updateLanguage.isError ? 'alert' : 'status'}
        >
          {message}
        </span>
      ) : null}
    </div>
  )
}
